// frontend/src/hooks/useBatchImport.js
//
// Reads an Excel file in the browser using SheetJS, slices it into
// batches of BATCH_SIZE rows, and POSTs each batch to the server.
// After each batch response we know EXACTLY how many rows have been
// processed, so the progress bar is 100% accurate.
//
// Usage:
//   const { startImport, state, reset } = useBatchImport({
//     endpoint:  '/api/pay-records/import-batch',
//     sheetName: 'Data',
//     batchSize: 500,       // optional, default 500
//   });
//
//   await startImport(file);   // pass the File object from the drop zone
//
// state shape:
//   {
//     status:    'idle' | 'reading' | 'importing' | 'done' | 'error'
//     totalRows: exact row count from the file
//     processed: rows sent and confirmed by server so far
//     pct:       0–100 (exact)
//     inserted:  running total
//     updated:   running total
//     skipped:   running total
//     errors:    running total
//     errorDetails: string[]
//     currentBatch: which batch number we're on
//     totalBatches: total batch count
//   }

import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

const DEFAULT_BATCH = 500;

export function useBatchImport({ endpoint, sheetName = 'Data', batchSize = DEFAULT_BATCH }) {
  const [state, setState] = useState({
    status: 'idle', totalRows: 0, processed: 0, pct: 0,
    inserted: 0, updated: 0, skipped: 0, errors: 0,
    errorDetails: [], currentBatch: 0, totalBatches: 0,
  });

  const abortRef = useRef(false);

  const reset = useCallback(() => {
    abortRef.current = true;
    setState({
      status: 'idle', totalRows: 0, processed: 0, pct: 0,
      inserted: 0, updated: 0, skipped: 0, errors: 0,
      errorDetails: [], currentBatch: 0, totalBatches: 0,
    });
  }, []);

  const startImport = useCallback(async (file) => {
    abortRef.current = false;

    // ── Step 1: Read the file in the browser ─────────────────────────────────
    setState(s => ({ ...s, status: 'reading', totalRows: 0 }));

    let allRows;
    try {
      allRows = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const wb   = XLSX.read(data, { type: 'array', cellDates: true });
            const ws   = wb.Sheets[sheetName];
            if (!ws) {
              reject(new Error(`Sheet "${sheetName}" not found in file`));
              return;
            }
            // sheet_to_json with defval keeps empty cells as empty string
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
            resolve(rows);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      setState(s => ({ ...s, status: 'error', errorDetails: [err.message] }));
      return;
    }

    if (!allRows || allRows.length === 0) {
      setState(s => ({ ...s, status: 'error', errorDetails: ['No data rows found in sheet'] }));
      return;
    }

    const totalRows    = allRows.length;
    const totalBatches = Math.ceil(totalRows / batchSize);
    const filename     = file.name;

    setState(s => ({
      ...s, status: 'importing', totalRows, totalBatches,
      processed: 0, pct: 0, currentBatch: 0,
    }));

    // ── Step 2: Read CSRF token from cookie ───────────────────────────────────
    const csrf = document.cookie
      .split('; ')
      .find(r => r.startsWith('csrf-token='))
      ?.split('=')[1] || '';

    // ── Step 3: Send batches one at a time ────────────────────────────────────
    let totalInserted = 0, totalUpdated = 0, totalSkipped = 0,
        totalErrors   = 0;
    const allErrorDetails = [];

    for (let i = 0; i < totalBatches; i++) {
      if (abortRef.current) break;

      const batch = allRows.slice(i * batchSize, (i + 1) * batchSize);

      try {
        const resp = await fetch(endpoint, {
          method:      'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrf,
          },
          credentials: 'include',
          body: JSON.stringify({ rows: batch, filename }),
        });

        if (!resp.ok) {
          const errJson = await resp.json().catch(() => ({}));
          throw new Error(errJson.message || `HTTP ${resp.status}`);
        }

        const result = await resp.json();
        totalInserted += result.inserted  || 0;
        totalUpdated  += result.updated   || 0;
        totalSkipped  += result.skipped   || 0;
        totalErrors   += result.errors    || 0;
        if (result.errorDetails?.length) {
          allErrorDetails.push(...result.errorDetails);
        }

      } catch (err) {
        totalErrors++;
        allErrorDetails.push(`Batch ${i + 1}: ${err.message}`);
      }

      const processed = Math.min((i + 1) * batchSize, totalRows);
      const pct       = Math.round((processed / totalRows) * 100);

      setState({
        status:       'importing',
        totalRows,
        processed,
        pct,
        inserted:     totalInserted,
        updated:      totalUpdated,
        skipped:      totalSkipped,
        errors:       totalErrors,
        errorDetails: allErrorDetails.slice(0, 20),
        currentBatch: i + 1,
        totalBatches,
      });
    }

    // ── Step 4: Done ──────────────────────────────────────────────────────────
    setState(s => ({
      ...s,
      status:    abortRef.current ? 'idle' : 'done',
      pct:       100,
      processed: totalRows,
    }));

  }, [endpoint, sheetName, batchSize]);

  return { startImport, state, reset };
}
